/**
 * Computes the passed in type presenting it as a monolithic one.
 * {@link https://www.totaltypescript.com/concepts/the-prettify-helper}
 *
 * @template T
 * @typedef {{
 *   [K in keyof T]: T[K];
 * } & {}} Prettify
 */

/**
 * Marks the type as one that needs to be definitely implemented.
 *
 * It serves two purposes:
 *   1. To disable type checker errors about type incompatibility.
 *   2. To quickly find types which must be implemented by searching
 *      by the reference or simply via project search.
 *
 * @typedef {any} TODO
 */

/**
 * @template {Constructor<object>} C
 * @typedef {C extends { prototype: infer P } ? P : never} PrototypeType
 */

/**
 * @template {object} Instance
 * @template {Array<unknown>} [Parameters=Array<unknown>]
 * @typedef {|
 *   Constructor<Instance>
 *   & (new (...parameters: Parameters) => Instance)
 * } InstantiableConstructor
 */

/**
 * @template {object} Instance
 * @typedef {{
 *   prototype: Instance;
 *   [Symbol.hasInstance](value: unknown): boolean;
 * }} Constructor
 */

/**
 * @typedef {Object} Stringifiable
 * @property {function(): string} toString
 */

/**
 * @template A
 * @typedef {Object} Serialisable
 * @property {function(): A} toJSON
 */

/**
 * @template A
 * @template {keyof A} K
 * @typedef {Required<Pick<A, K>> & Omit<A, K>} RequiredKeys
 */

/**
 * @template A
 * @template {keyof A} K
 * @typedef {Partial<Pick<A, K>> & Omit<A, K>} PartialKeys
 */

/**
 * @template {object} A
 * @param {A} constructor
 * @returns {{ new (): never } & A}
 */
export function liftConstructorType(constructor) {
  // @ts-expect-error by default TS allows to express constructor type
  // by only declaring **public** constructor expression. For classes
  // with **private** and **protected** classes type will not match
  // producing an error. By adding never-returning constructor, we mitigate
  // it "lifting constructor type" to be more general.
  // See also: https://stackoverflow.com/a/74657881
  return constructor;
}

/**
 * @template A
 * @template B
 * @typedef {A extends B ? A : B} Cast
 */
